import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * useHotKeys — power-trader keyboard bindings.
 *
 * Mounted once in App.jsx so shortcuts work across every page.
 * Skips when focus is inside an input/textarea/select/contenteditable
 * to avoid stealing keystrokes from forms.
 *
 * Bindings:
 *   ?            → open the help cheatsheet (toast)
 *   g d          → goto Dashboard (sequence)
 *   g p          → goto Profit Engine
 *   g a          → goto Analytics
 *   g r          → goto Risk
 *   g h          → goto Trading History
 *   g s          → goto Strategies
 *   /            → focus the global search if present
 *   k            → toggle kill-switch confirm modal (admin)
 *   shift + k    → also kill-switch (mirror)
 *   r            → refresh data on the current page (calls a global
 *                  custom event `apex:refresh` listened to by pages)
 *   esc          → close any open modal
 *   shift + d    → quick-download the audit pack PDF (when on
 *                  /profit-engine: triggers AUDIT PACK click)
 *
 * Sequences (`g d`, `g p`, …) require both keys within 1.5 s.
 */
const SEQUENCE_TIMEOUT_MS = 1500;
const ROUTE_MAP = {
  d: '/',
  p: '/profit-engine',
  a: '/analytics',
  r: '/risk',
  h: '/trade-history',
  s: '/strategies',
  i: '/intel',
  k: '/admin',
  o: '/portfolio',
};

const HELP_HTML = (
  '?  help · g d  dashboard · g p  profit · g a  analytics · '
  + 'g r  risk · g h  history · g s  strategies · '
  + '/  search · r  refresh · esc  close · shift+d  audit-pack'
);

export default function useHotKeys({ navigate, isAdmin }) {
  useEffect(() => {
    let pendingPrefix = null;
    let pendingTimer = null;

    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const onKey = (e) => {
      // Don't intercept while user is typing into a form.
      if (isTypingTarget(e.target)) return;
      // Don't intercept browser shortcuts.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key;

      // ESC closes the topmost modal (if our app dispatches one).
      if (key === 'Escape') {
        document.dispatchEvent(new CustomEvent('apex:hotkey:escape'));
        return;
      }

      // ? — help cheatsheet.
      if (key === '?') {
        e.preventDefault();
        toast.message('Hot-keys', { description: HELP_HTML, duration: 7000 });
        return;
      }

      // / — focus a global search if present (no-op otherwise).
      if (key === '/') {
        const search = document.querySelector(
          '[data-testid="global-search"], input[type="search"]',
        );
        if (search) {
          e.preventDefault();
          search.focus();
        }
        return;
      }

      // r — page refresh hook (emits a custom event pages can listen for).
      if (key === 'r') {
        document.dispatchEvent(new CustomEvent('apex:refresh'));
        return;
      }

      // shift+d — audit-pack quick-open (only on /profit-engine).
      if (e.shiftKey && (key === 'D' || key === 'd')) {
        const btn = document.querySelector(
          '[data-testid="audit-master-download-btn"]',
        );
        if (btn) {
          e.preventDefault();
          btn.click();
        }
        return;
      }

      // 'g <route>' sequence — start prefix or commit a route.
      if (pendingPrefix === 'g' && key in ROUTE_MAP) {
        e.preventDefault();
        clearTimeout(pendingTimer);
        pendingPrefix = null;
        const dest = ROUTE_MAP[key];
        if (typeof navigate === 'function') {
          navigate(dest);
        } else {
          window.location.href = dest;
        }
        toast.message(`→ ${dest}`, { duration: 1500 });
        return;
      }

      if (key === 'g') {
        pendingPrefix = 'g';
        clearTimeout(pendingTimer);
        pendingTimer = setTimeout(() => {
          pendingPrefix = null;
        }, SEQUENCE_TIMEOUT_MS);
        return;
      }

      // K (admin only) — quick-link to the kill-switch panel.
      if (isAdmin && (key === 'K')) {
        e.preventDefault();
        const elem = document.querySelector(
          '[data-testid="kill-switch-toggle"]',
        );
        if (elem) {
          elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
          toast.message('Kill-switch panel focused', { duration: 1500 });
        } else if (typeof navigate === 'function') {
          navigate('/risk');
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (pendingTimer) clearTimeout(pendingTimer);
    };
  }, [navigate, isAdmin]);
}
